import { BrowserChrome } from "@/components/products/shared";

const MONO = "font-[family-name:var(--font-mono)]";

export function MetaroomUpload() {
  const docs = [
    { name: "Q3 Financial Statements.pdf", status: "Analysed", indicator: "check" as const },
    { name: "Share Purchase Agreement.pdf", status: "Analysed", indicator: "check" as const },
    { name: "Due Diligence Checklist.xlsx", status: "Reading...", indicator: "amber" as const },
    { name: "Term Sheet Draft.pdf", status: "Queued", indicator: "queued" as const },
    { name: "Cap Table.csv", status: "Queued", indicator: "queued" as const },
  ];

  return (
    <BrowserChrome height="h-[320px]">
      <div className={`h-full text-[10px] ${MONO} flex gap-3`}>
        {/* Main panel */}
        <div className="flex-1 flex flex-col">
          {/* Drop zone */}
          <div className="border border-dashed border-[var(--color-border)] px-3 py-1.5 mb-2 text-center text-[var(--color-fg-tertiary)]">
            Drop files to upload
          </div>

          {/* Document list */}
          <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
            {docs.map((doc) => (
              <div
                key={doc.name}
                className="flex items-center justify-between px-2 py-1.5 border border-[var(--color-border)]"
              >
                <span className="text-[var(--color-fg-secondary)] truncate mr-2 text-[9px]">
                  {doc.name}
                </span>
                {doc.indicator === "check" && (
                  <span className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-500 whitespace-nowrap">
                    {doc.status} &#10003;
                  </span>
                )}
                {doc.indicator === "amber" && (
                  <span className="text-[8px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500 whitespace-nowrap flex items-center gap-1">
                    {doc.status}
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  </span>
                )}
                {doc.indicator === "queued" && (
                  <span className="text-[8px] px-1.5 py-0.5 rounded bg-[var(--color-border)] text-[var(--color-fg-tertiary)] whitespace-nowrap">
                    {doc.status}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar data panel */}
        <div className="w-28 border-l border-[var(--color-border)] pl-3 flex flex-col gap-3 pt-1">
          <div className="text-[var(--color-fg-tertiary)] text-[9px]">Extracted</div>
          <div>
            <div className="text-[var(--color-fg-tertiary)]">Revenue</div>
            <div className="text-[var(--color-fg)]">&pound;12.4M</div>
          </div>
          <div>
            <div className="text-[var(--color-fg-tertiary)]">EBITDA margin</div>
            <div className="text-[var(--color-fg)]">18%</div>
          </div>
          <div>
            <div className="text-[var(--color-fg-tertiary)]">Debt/equity</div>
            <div className="text-[var(--color-fg)]">0.4x</div>
          </div>
        </div>
      </div>
    </BrowserChrome>
  );
}

export function MetaroomResearch() {
  return (
    <BrowserChrome height="h-[320px]">
      <div className={`h-full text-[10px] ${MONO} flex flex-col`}>
        {/* Query bar */}
        <div className="border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 mb-4">
          <span className="text-[var(--color-fg-tertiary)] mr-1">&gt;</span>
          <span className="text-[var(--color-fg-secondary)]">
            Compare target EBITDA margin to sector median
          </span>
        </div>

        {/* Results table */}
        <div className="mb-4">
          <div className="text-[var(--color-fg-tertiary)] text-[9px] mb-2">Comparison results</div>
          <div className="border border-[var(--color-border)]">
            {/* Header */}
            <div className="flex border-b border-[var(--color-border)] bg-[var(--color-surface)]">
              <div className="flex-1 px-2 py-1.5 text-[var(--color-fg-tertiary)]">Metric</div>
              <div className="w-20 px-2 py-1.5 text-[var(--color-fg-tertiary)] text-right">Target</div>
              <div className="w-20 px-2 py-1.5 text-[var(--color-fg-tertiary)] text-right">Sector</div>
              <div className="w-24 px-2 py-1.5 text-[var(--color-fg-tertiary)] text-right">Top quartile</div>
            </div>
            {/* Row */}
            <div className="flex">
              <div className="flex-1 px-2 py-1.5 text-[var(--color-fg-secondary)]">EBITDA margin</div>
              <div className="w-20 px-2 py-1.5 text-emerald-500 text-right font-medium">18%</div>
              <div className="w-20 px-2 py-1.5 text-[var(--color-fg-secondary)] text-right">15.2%</div>
              <div className="w-24 px-2 py-1.5 text-[var(--color-fg-secondary)] text-right">22%</div>
            </div>
          </div>
        </div>

        {/* Source citation */}
        <div className="text-[var(--color-fg-tertiary)] text-[9px] mb-2">
          Source: uploaded Q3 financials + market data
        </div>

        {/* Audit trail link */}
        <div className="mt-auto flex justify-end">
          <span className="text-[var(--color-fg-tertiary)] text-[9px] border-b border-[var(--color-fg-tertiary)]">
            Audit trail
          </span>
        </div>
      </div>
    </BrowserChrome>
  );
}

export function MetaroomCompliance() {
  const checks = [
    { label: "FCA disclosure", status: "Pass", pass: true },
    { label: "AML source of funds", status: "Pass", pass: true },
    { label: "Concentration limits", status: "Flagged", pass: false },
    { label: "Investor suitability", status: "Pass", pass: true },
  ];

  return (
    <BrowserChrome height="h-[320px]">
      <div className={`h-full text-[10px] ${MONO} flex flex-col`}>
        <div className="text-[var(--color-fg-tertiary)] text-[9px] mb-3">Compliance dashboard</div>

        {/* Checklist */}
        <div className="space-y-0 mb-4">
          {checks.map((c) => (
            <div
              key={c.label}
              className="flex items-center justify-between py-2 px-2 border-b border-[var(--color-border)]"
            >
              <span className="text-[var(--color-fg-secondary)]">{c.label}</span>
              {c.pass ? (
                <span className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-500">
                  {c.status} &#10003;
                </span>
              ) : (
                <span className="text-[8px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500">
                  {c.status} &#9888;
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Review panel */}
        <div className="border border-[var(--color-border)] p-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] flex-shrink-0" />
            <div>
              <span className="text-[var(--color-fg-secondary)]">Assigned to: Compliance Officer</span>
              <span className="text-[var(--color-fg-tertiary)] ml-2">&middot; 2 hours ago</span>
            </div>
          </div>
        </div>

        {/* Export button */}
        <div className="mt-auto">
          <div className="inline-block px-3 py-1.5 text-[10px] border border-[var(--color-border)] text-[var(--color-fg-secondary)] bg-[var(--color-surface)]">
            Generate compliance report
          </div>
        </div>
      </div>
    </BrowserChrome>
  );
}
